import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Card,
  Typography,
  makeStyles,
  Theme,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  PrimaryButton,
  DefaultButton,
  Grid,
  GridSize,
  BbThemeProvider as ThemeProvider,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { toZonedTime } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import { DatePicker } from '@bb-ui/lab/dist/DatePicker';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StatusChip, CardSkeleton, useSkeletonState } from '../../shared';
import CohortStepper from './CohortStepper';
import { CohortDetail, SiteInCohort } from 'App.types';
import { useCohortNotifications } from '../../hooks/useCohortNotifications';
import { useDowntimeConflictValidation } from '../../hooks/useDowntimeConflictValidation';
import { isCohortEditable } from '../../utils';
import {
  combineDateAndTime,
  extractTimeString,
  formatDateToLocalString,
} from '../../utils/dateTimeHelpers';

type TimezoneOption = 'LOCAL' | 'UTC' | 'IST' | 'ET';

const TIMEZONE_MAP: Record<Exclude<TimezoneOption, 'LOCAL'>, string> = {
  UTC: 'UTC',
  IST: 'Asia/Kolkata',
  ET: 'America/New_York',
};

const getNextValidDate = (minDays: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + minDays);
  return d;
};

const createDateFromTz = (dateString: string, timeZone: string): Date => {
  const testDate = new Date(`${dateString}Z`);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(testDate);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value;

  let offset = 'Z';
  if (offsetPart && offsetPart !== 'GMT') {
    const rawOffset = offsetPart.replace('GMT', '');
    const sign = rawOffset[0];
    const timeParts = rawOffset.substring(1).split(':');
    const h = timeParts[0].padStart(2, '0');
    const m = timeParts[1] ? timeParts[1].padStart(2, '0') : '00';
    offset = `${sign}${h}:${m}`;
  }

  return new Date(`${dateString}:00${offset}`);
};

interface SummaryItem {
  label: string;
  value: string | number;
}

interface SiteInfo {
  captain_id: string;
  learn_url?: string;
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  site_status?: string;
  notifications?: {
    status?: string;
    migration_date?: string;
    start_suggested_date?: string;
    end_suggested_date?: string;
    resend_number?: number;
    is_accepted?: boolean;
  };
}

interface CohortSummaryCardProps {
  cohort: CohortDetail;
  isDowntimeDisabled: boolean;
  onUpdateDowntime: (date: string) => void;
  onDowntimeConflictDetected?: (pendingDate: string, conflictingSites: SiteInCohort[]) => void;
  onSendNotifications: () => void;
  onSendReminders?: () => void;
  loadingNotifications: boolean;
  loadingReminders: boolean;
  loadingDowntime: boolean;
  sites: SiteInfo[];
  isLoadingSites?: boolean;
  isRefreshingStatus?: boolean;
  enableNotifications?: boolean;
  onSelectedTimezoneChange?: (info: { timeZone: string | undefined; label: string }) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  summaryCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
  },
  statusContainer: {
    width: '100%',
  },
  dateTimeRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  datePickerWrapper: {
    flex: 1,
    minWidth: 0,
    maxWidth: '200px',
  },
  datePickerPaper: {
    width: '262px !important',
    '& div[role="grid"]': {
      display: 'block !important',
    },
    '& .MuiCalendarPicker-viewTransitionContainer': {
      width: '238px !important',
    },
    '& .MuiPickersDay-root': {
      width: '32px !important',
      height: '32px !important',
      borderRadius: '2px !important',
      margin: '0 !important',
      fontSize: '0.875rem',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: `${theme.palette.brand?.main || theme.palette.primary.main} !important`,
      color: `${theme.palette.brand?.contrastText || theme.palette.primary.contrastText} !important`,
      fontWeight: 'bold !important',
      '&:hover, &:focus': {
        backgroundColor: `${theme.palette.brand?.main || theme.palette.primary.main} !important`,
      },
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      color: `${theme.palette.brand?.main || theme.palette.primary.main} !important`,
      fontWeight: 'bold !important',
      border: 'none !important',
    },
    '& .MuiDayPicker-header': {
      display: 'none',
    },
  },
  timeField: {
    flex: '0 0 130px',
    marginTop: 0,
  },
  dateFieldUpdating: {
    opacity: 0.7,
  },
  timezoneRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1.5),
    width: '100%',
  },
  timezoneToggleLabel: {
    fontWeight: 500,
    marginRight: theme.spacing(2),
    marginTop: '6px',
    whiteSpace: 'nowrap',
  },
  timezoneControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  timezoneHelperText: {
    margin: '4px 0 0 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '100%',
  },
  loadingOverlay: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      borderRadius: theme.shape.borderRadius,
      pointerEvents: 'none',
    },
  },
  errorHelperText: {
    marginTop: theme.spacing(0.5),
  },
}));

const getUserTimezoneInfo = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  const timeString = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(now);

  return { timezone, timeString };
};

const AWS_REGIONS_MAP: Record<string, string> = {
  'us-east-1': 'US East (N. Virginia)',
  'us-west-2': 'US West (Oregon)',
  'eu-central-1': 'Europe (Frankfurt)',
  'eu-west-1': 'Europe (Ireland)',
  'ca-central-1': 'Canada (Central)',
  'ap-southeast-1': 'Asia Pacific (Singapore)',
  'ap-southeast-2': 'Asia Pacific (Sydney)',
};

const CohortSummaryCard: React.FC<CohortSummaryCardProps> = ({
  cohort,
  isDowntimeDisabled,
  onUpdateDowntime,
  onDowntimeConflictDetected,
  onSendNotifications,
  onSendReminders,
  loadingNotifications = false,
  loadingReminders = false,
  loadingDowntime = false,
  sites = [],
  isLoadingSites = false,
  isRefreshingStatus = false,
  enableNotifications = true,
  onSelectedTimezoneChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showSkeleton, showLoadingOverlay } = useSkeletonState(!cohort, !!cohort);

  const { notificationData, getReminderStatusText } = useCohortNotifications({
    cohort,
    sites,
  });

  const { validateDowntimeAgainstSites } = useDowntimeConflictValidation();

  const { timezone } = getUserTimezoneInfo();
  const [selectedViewTz, setSelectedViewTz] = React.useState<TimezoneOption>('LOCAL');
  const [downtimeError, setDowntimeError] = React.useState<string>('');
  const [localDate, setLocalDate] = useState<Date | null>(null);
  const [localTime, setLocalTime] = useState<string>('');
  const [pendingUtcDate, setPendingUtcDate] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getTargetTimezone = useCallback(
    () => (selectedViewTz === 'LOCAL' ? timezone : TIMEZONE_MAP[selectedViewTz]),
    [selectedViewTz, timezone],
  );

  useEffect(() => {
    if (onSelectedTimezoneChange) {
      onSelectedTimezoneChange({
        timeZone: selectedViewTz === 'LOCAL' ? undefined : TIMEZONE_MAP[selectedViewTz],
        label: selectedViewTz === 'LOCAL' ? t('cohorts.local') : selectedViewTz,
      });
    }
  }, [selectedViewTz, onSelectedTimezoneChange, t]);

  const safeMinDate = useMemo(() => getNextValidDate(15), []);

  useEffect(() => {
    if (cohort?.downtime_date) {
      try {
        const zonedDate = toZonedTime(new Date(cohort.downtime_date), getTargetTimezone());
        setLocalDate(zonedDate);
        setLocalTime(extractTimeString(zonedDate));
      } catch {
        setLocalDate(null);
        setLocalTime('');
      }
    } else {
      setLocalDate(null);
      setLocalTime('');
    }
  }, [cohort?.downtime_date, getTargetTimezone]);

  const datePickerStrings = useMemo(
    () => ({
      monthLabel: t('cohorts.month', 'Month'),
      monthNextLabel: t('cohorts.nextMonth', 'Next month'),
      monthPreviousLabel: t('cohorts.previousMonth', 'Previous month'),
      selectedDayLabel: (day: string) => `${day} - ${t('cohorts.selected', 'Selected')}`,
      yearLabel: t('cohorts.year', 'Year'),
    }),
    [t],
  );

  const getRegionDisplay = useCallback((region?: string): string => {
    if (!region) return '-';
    return AWS_REGIONS_MAP[region] || region;
  }, []);

  const textItems = useMemo(
    () => ({
      name: { label: t('cohorts.name'), value: cohort?.name || '-' },
      id: { label: t('cohorts.cohortId'), value: cohort?.id || '-' },
      createdBy: { label: t('cohorts.createdBy'), value: cohort?.created_by_user || '-' },
      sites: {
        label: t('cohorts.sitesCount'),
        value: isLoadingSites ? '...' : sites.length,
      },
      fleetId: { label: t('cohorts.fleetId'), value: cohort?.fleet_id || '-' },
      awsAccount: { label: t('cohorts.awsAccount'), value: cohort?.fleet_account_id || '-' },
      region: {
        label: t('cohorts.fleetRegion'),
        value: getRegionDisplay(cohort?.fleet_region),
      },
    }),
    [
      t,
      cohort?.name,
      cohort?.id,
      cohort?.created_by_user,
      cohort?.fleet_id,
      cohort?.fleet_account_id,
      cohort?.fleet_region,
      sites.length,
      isLoadingSites,
      getRegionDisplay,
    ],
  );

  const statusHelperText = useMemo(() => {
    if (!isDowntimeDisabled) {
      return '';
    }
    return !isCohortEditable(cohort?.migration_status)
      ? t('cohorts.downtimeEditDisabled')
      : t('cohorts.downtimeLocked');
  }, [isDowntimeDisabled, cohort?.migration_status, t]);

  const timezoneHelperText = selectedViewTz === 'LOCAL' ? timezone : '';

  const handleTimezoneChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newTz: TimezoneOption | null) => {
      if (newTz !== null && !loadingDowntime) {
        setSelectedViewTz(newTz);
      }
    },
    [loadingDowntime],
  );

  const validateAndSubmit = useCallback(
    (date: Date | null, time: string) => {
      if (!date || !time || isDowntimeDisabled) return;

      const combined = combineDateAndTime(date, time);
      if (!combined || Number.isNaN(combined.getTime())) return;

      const targetTimezone = getTargetTimezone();
      let utcDate: Date;
      try {
        const dateStr = formatDateToLocalString(combined);
        utcDate = createDateFromTz(dateStr, targetTimezone);
      } catch {
        return;
      }
      if (Number.isNaN(utcDate.getTime())) return;

      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const minDate = new Date(now);
      minDate.setDate(minDate.getDate() + 15);

      if (utcDate < minDate) {
        setDowntimeError(t('cohorts.validation.upgradeDateTooSoon', { minDays: 15 }));
        return;
      }

      if (cohort?.downtime_date) {
        const originalUtc = new Date(cohort.downtime_date).getTime();
        if (utcDate.getTime() === originalUtc) return;
      }

      if (sites.length > 0) {
        const { hasConflicts, conflictingSites: conflicting } = validateDowntimeAgainstSites(
          utcDate,
          sites,
        );

        if (hasConflicts) {
          if (cohort?.downtime_date) {
            const zonedOriginal = toZonedTime(new Date(cohort.downtime_date), targetTimezone);
            setLocalDate(zonedOriginal);
            setLocalTime(extractTimeString(zonedOriginal));
          }
          if (onDowntimeConflictDetected) {
            onDowntimeConflictDetected(utcDate.toISOString(), conflicting as SiteInCohort[]);
          }
          return;
        }
      }

      setDowntimeError('');
      setPendingUtcDate(utcDate.toISOString());
      setConfirmOpen(true);
    },
    [
      isDowntimeDisabled,
      getTargetTimezone,
      t,
      cohort?.downtime_date,
      sites,
      validateDowntimeAgainstSites,
      onDowntimeConflictDetected,
    ],
  );

  const handleDatePickerChange = useCallback(
    (newDate: Date | null) => {
      setLocalDate(newDate);
      if (newDate && !Number.isNaN(newDate.getTime())) {
        setDowntimeError('');
      }
      if (newDate && !Number.isNaN(newDate.getTime()) && localTime) {
        validateAndSubmit(newDate, localTime);
      }
    },
    [localTime, validateAndSubmit],
  );

  const handleTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTime(e.target.value);
    if (e.target.value) {
      setDowntimeError('');
    }
  }, []);

  const handleTimeBlur = useCallback(() => {
    if (localDate && !Number.isNaN(localDate.getTime()) && localTime) {
      validateAndSubmit(localDate, localTime);
    }
  }, [localDate, localTime, validateAndSubmit]);

  const handleConfirmDowntime = useCallback(() => {
    if (pendingUtcDate) {
      onUpdateDowntime(pendingUtcDate);
    }
    setPendingUtcDate(null);
    setConfirmOpen(false);
  }, [pendingUtcDate, onUpdateDowntime]);

  const handleCancelDowntime = useCallback(() => {
    if (cohort?.downtime_date) {
      const zonedOriginal = toZonedTime(new Date(cohort.downtime_date), getTargetTimezone());
      setLocalDate(zonedOriginal);
      setLocalTime(extractTimeString(zonedOriginal));
    } else {
      setLocalDate(null);
      setLocalTime('');
    }
    setPendingUtcDate(null);
    setConfirmOpen(false);
  }, [cohort?.downtime_date, getTargetTimezone]);

  const renderSummaryItem = useCallback(
    (item: SummaryItem, key: string, xs: GridSize) => (
      <Grid size={{ xs }} className={classes.summaryItem} key={key}>
        <Typography variant="subtitle2" color="textSecondary">
          {item.label}
        </Typography>
        <Typography variant="body1" color="textPrimary" style={{ wordBreak: 'break-word' }}>
          {item.value}
        </Typography>
      </Grid>
    ),
    [classes.summaryItem],
  );

  const renderStatusSection = useCallback(
    () => (
      <Grid size={{ xs: 4 }} className={classes.summaryItem} key="status">
        <Typography variant="subtitle2" color="textSecondary">
          {t('cohorts.statusLabel')}
        </Typography>
        <div className={classes.statusContainer}>
          <StatusChip
            status={cohort?.migration_status}
            loading={isRefreshingStatus}
            enableNotifications={enableNotifications}
          />
        </div>
      </Grid>
    ),
    [
      classes.summaryItem,
      classes.statusContainer,
      cohort?.migration_status,
      isRefreshingStatus,
      t,
      enableNotifications,
    ],
  );

  const renderDateSection = useCallback(
    () => (
      <Grid size={{ xs: 5 }} className={classes.summaryItem} key="date">
        <Typography variant="subtitle2" color="textSecondary">
          {t('cohorts.downtimeDate')} (
          {selectedViewTz === 'LOCAL' ? t('cohorts.local') : selectedViewTz})
        </Typography>

        <div
          className={`${classes.dateTimeRow} ${loadingDowntime ? classes.dateFieldUpdating : ''}`}
        >
          <div className={classes.datePickerWrapper}>
            <ThemeProvider theme="light">
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                <DatePicker
                  id="downtime-date-picker"
                  label=""
                  value={localDate}
                  onChange={handleDatePickerChange}
                  shouldDisableDate={(date: Date) => date < safeMinDate}
                  disabled={isDowntimeDisabled || loadingDowntime}
                  strings={datePickerStrings}
                  PopperProps={{
                    container: document.body,
                    style: { zIndex: 1400 },
                  }}
                  PaperProps={{
                    className: classes.datePickerPaper,
                  }}
                  TextFieldProps={{
                    size: 'small',
                    error: !!downtimeError,
                    fullWidth: true,
                  }}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </div>

          <TextField
            id="downtime-time"
            type="time"
            value={localTime}
            onChange={handleTimeChange}
            onBlur={handleTimeBlur}
            size="small"
            disabled={isDowntimeDisabled || loadingDowntime}
            error={!!downtimeError}
            className={classes.timeField}
            inputProps={{ step: 300, 'aria-label': t('cohorts.time', 'Time') }}
          />
        </div>

        {(downtimeError || statusHelperText) && (
          <FormHelperText error={!!downtimeError} className={classes.errorHelperText}>
            {downtimeError || statusHelperText}
          </FormHelperText>
        )}

        <div className={classes.timezoneRow}>
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.timezoneToggleLabel}
          >
            {t('cohorts.timezone')}
          </Typography>

          <div className={classes.timezoneControlContainer}>
            <ToggleButtonGroup
              value={selectedViewTz}
              exclusive
              onChange={handleTimezoneChange}
              size="small"
              style={{ display: 'flex', width: '100%' }}
            >
              <ToggleButton value="LOCAL" disabled={loadingDowntime} style={{ flex: 1 }}>
                {t('cohorts.local')}
              </ToggleButton>
              <ToggleButton value="UTC" disabled={loadingDowntime} style={{ flex: 1 }}>
                UTC
              </ToggleButton>
              <ToggleButton value="IST" disabled={loadingDowntime} style={{ flex: 1 }}>
                IST
              </ToggleButton>
              <ToggleButton value="ET" disabled={loadingDowntime} style={{ flex: 1 }}>
                ET
              </ToggleButton>
            </ToggleButtonGroup>

            {timezoneHelperText && (
              <FormHelperText className={classes.timezoneHelperText}>
                {timezoneHelperText}
              </FormHelperText>
            )}
          </div>
        </div>
      </Grid>
    ),
    [
      classes.summaryItem,
      classes.dateTimeRow,
      classes.dateFieldUpdating,
      classes.datePickerWrapper,
      classes.datePickerPaper,
      classes.timeField,
      classes.errorHelperText,
      classes.timezoneRow,
      classes.timezoneToggleLabel,
      classes.timezoneControlContainer,
      classes.timezoneHelperText,
      t,
      selectedViewTz,
      localDate,
      localTime,
      handleDatePickerChange,
      handleTimeChange,
      handleTimeBlur,
      isDowntimeDisabled,
      loadingDowntime,
      downtimeError,
      statusHelperText,
      timezoneHelperText,
      safeMinDate,
      datePickerStrings,
      handleTimezoneChange,
    ],
  );

  const renderStepperSection = useCallback(
    () => (
      <Grid
        size={{ xs: 4 }}
        className={`${classes.summaryItem} ${showLoadingOverlay ? classes.loadingOverlay : ''}`}
        key="stepper"
      >
        {cohort.enable_notifications ? (
          <CohortStepper
            cohort={cohort!}
            onSendNotifications={onSendNotifications}
            onSendReminders={onSendReminders}
            loadingNotifications={loadingNotifications}
            loadingReminders={loadingReminders}
            sites={sites}
            notificationData={notificationData}
            reminderStatusText={getReminderStatusText()}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t('cohorts.sendNotificationsDisabled')}
          </Typography>
        )}
      </Grid>
    ),
    [
      classes.summaryItem,
      classes.loadingOverlay,
      cohort,
      onSendNotifications,
      onSendReminders,
      loadingNotifications,
      loadingReminders,
      sites,
      notificationData,
      showLoadingOverlay,
      t,
      getReminderStatusText,
    ],
  );

  if (showSkeleton) {
    return (
      <Card className={classes.summaryCard} variant="outlined">
        <Grid container spacing={2}>
          <CardSkeleton
            items={[
              { label: true, value: 'wide', gridSize: 3 },
              { label: true, value: 'medium', gridSize: 4 },
              { label: true, value: 'medium', type: 'chip', gridSize: 5 },
              { label: true, value: 'medium', gridSize: 3 },
              { label: true, value: 'medium', gridSize: 4 },
              { label: true, value: 'medium', gridSize: 5 },
              { label: true, value: 'narrow', gridSize: 3 },
              { label: true, value: 'full', type: 'input', gridSize: 4 },
              { label: false, value: 'full', gridSize: 5, type: 'button' },
            ]}
          />
        </Grid>
      </Card>
    );
  }

  return (
    <>
      <Card className={classes.summaryCard} variant="outlined">
        <Grid container spacing={2}>
          {renderSummaryItem(textItems.name, 'name', 3)}
          {renderSummaryItem(textItems.id, 'id', 5)}
          {renderStatusSection()}

          {renderSummaryItem(textItems.createdBy, 'createdBy', 3)}
          {renderSummaryItem(textItems.awsAccount, 'aws', 5)}
          {renderSummaryItem(textItems.fleetId, 'fleetId', 4)}

          {renderSummaryItem(textItems.sites, 'sites', 3)}
          {renderDateSection()}
          {renderStepperSection()}
        </Grid>
      </Card>

      <Dialog open={confirmOpen} onClose={handleCancelDowntime} maxWidth="xs" fullWidth>
        <DialogTitle onClose={handleCancelDowntime} closeLabel={t('global.close')}>
          {t('cohorts.confirmDowntimeChange.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{t('cohorts.confirmDowntimeChange.message')}</Typography>
          <Typography variant="body2" style={{ marginTop: 12, fontWeight: 600 }}>
            {pendingUtcDate
              ? new Date(pendingUtcDate).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={handleCancelDowntime}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton onClick={handleConfirmDowntime}>
            {t('cohorts.confirmDowntimeChange.confirm')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CohortSummaryCard;
